import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Icon } from '@mdi/react';
import { mdiChevronDown, mdiChevronRight } from '@mdi/js';
import { Field, Formik, FormikProps } from 'formik';
import _ from 'lodash';
import styled from '@emotion/styled';
import { getOptions } from '../../../base/select';
import * as S from '../form.styles';
import * as CS from '../section.styles';
import { ServiceLineProvidersForm } from './service-line-providers-form';
import { ServiceLineFacilityForm } from './service-line-facility-form';
import { ClaimEditorStore } from '../../../../stores/claim-editor';
import { PlaceOfServiceType, DrugMeasurementCode } from '../../../../api';
import { InputField, SelectField } from '../../../base/form';
import { DatePickerField } from '../../../base/form/datepicker/date-picker';
import { AreaField } from '../../../base/form/area/area';
import { BaseCheckbox } from '../../../base/checkbox/checkbox';
import { BaseButton } from '../../../base/button/button';
import * as Yup from 'yup';
import { FormErrorBanner } from '../../../base/form/form-error-banner';
import {
    normalizeServiceLineDiagnosisCodes,
    normalizedDiagnosisCodesForPayload,
} from '../../../../utils/normalize-service-line-diagnosis-codes';

const CodesGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(6, 1fr);
    gap: 16px;
    align-items: start;

    @media (max-width: 1400px) {
        grid-template-columns: repeat(4, 1fr);
    }

    @media (max-width: 1024px) {
        grid-template-columns: repeat(3, 1fr);
        gap: 12px;
    }

    @media (max-width: 768px) {
        grid-template-columns: repeat(2, 1fr);
        gap: 12px;
    }

    @media (max-width: 480px) {
        grid-template-columns: 1fr;
        gap: 10px;
    }
`;

const CollapsibleToggleButton = styled(BaseButton)`
    min-width: 0;
    padding: 0 10px;
    background: transparent;
    border: none;
`;

interface IServiceItemFormProps {
    claimEditorStore: ClaimEditorStore;
    serviceIndex: number;
    initialCodes?: string[];
    initialPlaceOfService?: PlaceOfServiceType | '';
    initialPrimaryAuth?: string;
    initialSecondaryAuth?: string;
    initialTertiaryAuth?: string;
    initialNarrative?: string;
    initialEmergency?: boolean;
    initialFromDos?: string;
    initialThroughDos?: string;
    initialProcedureCode?: string;
    initialMod1?: string;
    initialMod2?: string;
    initialMod3?: string;
    initialMod4?: string;
    initialUnits?: number | string;
    initialDrugQuantity?: number | string;
    initialDrugNdc?: string;
    initialDrugMeasurement?: string;
}

interface ServiceFormValues {
    fromDos: string;
    throughDos: string;
    procedureCode: string;
    mod1: string;
    mod2: string;
    mod3: string;
    mod4: string;
    units: string;
    codes: string[];
    placeOfService: PlaceOfServiceType | '';
    primaryAuth: string;
    secondaryAuth: string;
    tertiaryAuth: string;
    narrative: string;
    emergency: boolean;
    drugQuantity: string;
    drugNdc: string;
    drugMeasurement: string;
}

const ServiceSchema = Yup.object().shape({
    fromDos: Yup.string().required('From Date of Service is required'),
    throughDos: Yup.string(),
    procedureCode: Yup.string().required('Procedure Code is required'),
    mod1: Yup.string(),
    mod2: Yup.string(),
    mod3: Yup.string(),
    mod4: Yup.string(),
    units: Yup.number()
        .transform((value, originalValue) => (originalValue === '' ? undefined : value))
        .typeError('Units must be a number')
        .integer('Units must be a whole number')
        .moreThan(0, 'Units must be greater than 0')
        .required('Units is required'),
    codes: Yup.array().of(Yup.string()).min(1, 'At least one diagnosis code is required'),
    placeOfService: Yup.string().required('Place of Service is required'),
    primaryAuth: Yup.string(),
    secondaryAuth: Yup.string(),
    tertiaryAuth: Yup.string(),
    narrative: Yup.string(),
    emergency: Yup.boolean(),
});

export const ServiceItemForm: React.FunctionComponent<IServiceItemFormProps> = ({
    claimEditorStore,
    serviceIndex,
    initialCodes,
    initialPlaceOfService = '',
    initialPrimaryAuth = '',
    initialSecondaryAuth = '',
    initialTertiaryAuth = '',
    initialNarrative = '',
    initialEmergency = false,
    initialFromDos = '',
    initialThroughDos = '',
    initialProcedureCode = '',
    initialMod1 = '',
    initialMod2 = '',
    initialMod3 = '',
    initialMod4 = '',
    initialUnits = '',
    initialDrugQuantity = '',
    initialDrugNdc = '',
    initialDrugMeasurement = '',
}) => {
    const posOptions = getOptions(PlaceOfServiceType);
    const drugMeasurementOptions = getOptions(DrugMeasurementCode);
    const formikRef = useRef<FormikProps<ServiceFormValues> | null>(null);

    const initCodes = useMemo(
        () => normalizeServiceLineDiagnosisCodes(initialCodes || []),
        [initialCodes],
    );

    const initialValues = useMemo<ServiceFormValues>(
        () => ({
            fromDos: initialFromDos || '',
            throughDos: initialThroughDos || '',
            procedureCode: initialProcedureCode || '',
            mod1: initialMod1 || '',
            mod2: initialMod2 || '',
            mod3: initialMod3 || '',
            mod4: initialMod4 || '',
            units: String(initialUnits ?? ''),
            codes: initCodes,
            placeOfService: initialPlaceOfService || ('' as any),
            primaryAuth: initialPrimaryAuth || '',
            secondaryAuth: initialSecondaryAuth || '',
            tertiaryAuth: initialTertiaryAuth || '',
            narrative: initialNarrative || '',
            emergency: !!initialEmergency,
            drugQuantity: initialDrugQuantity ? String(initialDrugQuantity) : '',
            drugNdc: initialDrugNdc || '',
            drugMeasurement: initialDrugMeasurement || '',
        }),
        [
            initialFromDos,
            initialThroughDos,
            initialProcedureCode,
            initialMod1,
            initialMod2,
            initialMod3,
            initialMod4,
            initialUnits,
            initCodes,
            initialPlaceOfService,
            initialPrimaryAuth,
            initialSecondaryAuth,
            initialTertiaryAuth,
            initialNarrative,
            initialEmergency,
            initialDrugQuantity,
            initialDrugNdc,
            initialDrugMeasurement,
        ],
    );

    const convertToService = useCallback((v: ServiceFormValues): any => {
        const diagnosisCodes = normalizedDiagnosisCodesForPayload(v.codes);
        const procedureModifierCodes = [v.mod1, v.mod2, v.mod3, v.mod4].filter(Boolean);
        return {
            fromDateOfService: v.fromDos || undefined,
            throughDateOfService: v.throughDos || undefined,
            procedureCode: v.procedureCode || '',
            procedureModifierCodes: procedureModifierCodes.length > 0 ? procedureModifierCodes : undefined,
            units: v.units && Number(v.units) > 0 ? Number(v.units) : undefined,
            diagnosisCodes: diagnosisCodes.length > 0 ? diagnosisCodes : undefined,
            placeOfService: v.placeOfService || undefined,
            primaryPriorAuthorizationNumber: v.primaryAuth || undefined,
            secondaryPriorAuthorizationNumber: v.secondaryAuth || undefined,
            tertiaryPriorAuthorizationNumber: v.tertiaryAuth || undefined,
            narrative: v.narrative || undefined,
            emergency: !!v.emergency,
            // Do NOT include drug fields here; they are handled separately via updateServiceLineDrug
        };
    }, []);

    const convertToDrug = useCallback(
        (
            v: ServiceFormValues,
        ): { x12NationalDrugCode?: string; x12DrugQuantity?: string; x12DrugMeasurement?: string } | null => {
            // Only create drug object if at least one field has a value
            if (v.drugQuantity || v.drugNdc || v.drugMeasurement) {
                return {
                    x12NationalDrugCode: v.drugNdc || undefined,
                    x12DrugQuantity: v.drugQuantity ? String(v.drugQuantity) : undefined,
                    x12DrugMeasurement: v.drugMeasurement || undefined,
                };
            }
            return null;
        },
        [],
    );

    const debouncedSave = useCallback(
        _.debounce(() => {
            const latestValues = formikRef.current?.values;
            if (!latestValues) return;
            const serviceData = convertToService(latestValues);
            // Do NOT include facility/providers/drug here; they are handled separately
            claimEditorStore.updateServiceLine(serviceIndex, serviceData);

            // Save drug data separately using the nested structure
            const drugData = convertToDrug(latestValues);
            claimEditorStore.updateServiceLineDrug(serviceIndex, drugData);
        }, 500),
        [claimEditorStore, serviceIndex, convertToService, convertToDrug],
    );

    const persistService = useCallback(() => {
        const latestValues = formikRef.current?.values;
        if (!latestValues) return;
        const serviceData = convertToService(latestValues);
        claimEditorStore.updateServiceLine(serviceIndex, serviceData);
        const drugData = convertToDrug(latestValues);
        claimEditorStore.updateServiceLineDrug(serviceIndex, drugData);
    }, [claimEditorStore, serviceIndex, convertToService, convertToDrug]);

    useEffect(() => {
        return () => {
            debouncedSave.cancel();
        };
    }, [debouncedSave]);

    // Determine if nested sections already have data so we can default them expanded
    const svc: any =
        (claimEditorStore.services || [])[serviceIndex] ||
        ((claimEditorStore.activePayload as any)?.services || [])[serviceIndex] ||
        {};
    const hasExistingProviders = Array.isArray(svc.providers) && svc.providers.length > 0;
    const hasExistingFacility = !!svc.facility;
    const hasExistingDrug =
        !!svc.drug?.x12NationalDrugCode || !!svc.drug?.x12DrugQuantity || !!svc.drug?.x12DrugMeasurement;

    const [showDrug, setShowDrug] = useState(hasExistingDrug);
    const [showProviders, setShowProviders] = useState(hasExistingProviders);
    const [showFacility, setShowFacility] = useState(hasExistingFacility);
    const [maxUnlockedCodeIndex, setMaxUnlockedCodeIndex] = useState(0);
    const focusCodeInput = useCallback((index: number) => {
        const next = document.querySelector<HTMLInputElement>(`input[name="codes.${index}"]`);
        next?.focus();
        next?.select();
    }, []);
    const unlockAndFocusNextCode = useCallback(
        (idx: number) => {
            const nextIdx = idx + 1;
            if (nextIdx >= 12) return;
            setMaxUnlockedCodeIndex(prev => Math.max(prev, nextIdx));
            // Wait one paint for disabled state to reflect, then focus target input.
            requestAnimationFrame(() => focusCodeInput(nextIdx));
        },
        [focusCodeInput],
    );

    return (
        <Formik<ServiceFormValues>
            innerRef={formikRef as any}
            initialValues={initialValues}
            validationSchema={ServiceSchema}
            onSubmit={() => {}}
            enableReinitialize
            validateOnMount={false}
            validateOnChange={true}
            validateOnBlur={false}>
            {formik => {
                // Auto-save on value change - save whenever values change, not just when dirty
                useEffect(() => {
                    debouncedSave();
                }, [formik.values, debouncedSave]);

                useEffect(() => {
                    if (claimEditorStore.validationSignal > 0) {
                        // Flush pending service updates so submit validation reads latest values.
                        debouncedSave.cancel();
                        persistService();
                        formik.submitForm();
                    }
                }, [claimEditorStore.validationSignal, debouncedSave, persistService]);

                const firstEmptyIndex = formik.values.codes.findIndex(c => !c);
                const parsedUnits = Number(formik.values.units);
                const isUnitsNonWhole =
                    formik.values.units !== '' && Number.isFinite(parsedUnits) && !Number.isInteger(parsedUnits);

                return (
                    <S.FormContainer>
                        <FormErrorBanner
                            submitCount={formik.submitCount}
                            isValid={formik.isValid}
                            errors={formik.errors}
                        />
                        <S.FormGrid>
                            <S.FormField>
                                <S.Label required>From DoS</S.Label>
                                <Field name="fromDos" component={DatePickerField} />
                            </S.FormField>
                            <S.FormField>
                                <S.Label>Through DoS</S.Label>
                                <Field name="throughDos" component={DatePickerField} />
                            </S.FormField>
                            <S.FormField>
                                <S.Label required>Procedure Code</S.Label>
                                <Field name="procedureCode" placeholder="Procedure Code" component={InputField} />
                            </S.FormField>
                            <S.FormField>
                                <S.Label>Mod 1</S.Label>
                                <Field name="mod1" placeholder="Mod 1" component={InputField} />
                            </S.FormField>
                            <S.FormField>
                                <S.Label>Mod 2</S.Label>
                                <Field name="mod2" placeholder="Mod 2" component={InputField} />
                            </S.FormField>
                            <S.FormField>
                                <S.Label>Mod 3</S.Label>
                                <Field name="mod3" placeholder="Mod 3" component={InputField} />
                            </S.FormField>
                            <S.FormField>
                                <S.Label>Mod 4</S.Label>
                                <Field name="mod4" placeholder="Mod 4" component={InputField} />
                            </S.FormField>
                            <S.FormField>
                                <S.Label required>Units</S.Label>
                                <Field name="units" type="number" step={1} placeholder="Units" component={InputField} />
                                {isUnitsNonWhole && (
                                    <S.WarningMessage>
                                        This is not a whole number. Are you sure this is correct?
                                    </S.WarningMessage>
                                )}
                            </S.FormField>
                        </S.FormGrid>
                        <CodesGrid>
                            {formik.values.codes.map((code, idx) => {
                                const isEmpty = !code;
                                const enabled = !isEmpty || idx === firstEmptyIndex || idx <= maxUnlockedCodeIndex;
                                return (
                                    <S.FormField key={idx}>
                                        <S.Label>
                                            {`Code ${idx + 1}`}
                                            {idx === 0 ? ' *' : ''}
                                        </S.Label>
                                        <Field
                                            name={`codes.${idx}`}
                                            placeholder={`Code ${idx + 1}`}
                                            component={InputField}
                                            disabled={!enabled}
                                            onChange={(value: string, form: any) => {
                                                const next = [...form.values.codes];
                                                next[idx] = value ?? '';
                                                const normalized = normalizeServiceLineDiagnosisCodes(next);
                                                // InputField runs Formik's field onChange after this; it would overwrite
                                                // `codes.${idx}` with the raw blur value. Re-apply after that tick.
                                                queueMicrotask(() => {
                                                    form.setFieldValue('codes', normalized);
                                                });
                                            }}
                                            onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                                                const isForwardTab = e.key === 'Tab' && !e.shiftKey;
                                                if (
                                                    (e.key === 'Enter' || isForwardTab) &&
                                                    idx < formik.values.codes.length - 1
                                                ) {
                                                    e.preventDefault();
                                                    unlockAndFocusNextCode(idx);
                                                }
                                            }}
                                        />
                                    </S.FormField>
                                );
                            })}
                        </CodesGrid>

                        <S.FormGrid>
                            <S.FormField>
                                <S.Label required>Place of Service</S.Label>
                                <Field
                                    name="placeOfService"
                                    component={SelectField}
                                    options={posOptions as any}
                                    placeholder="Select"
                                    isClearable={false}
                                />
                            </S.FormField>

                            <S.FormField>
                                <S.Label>Primary Prior Authorization Number</S.Label>
                                <Field
                                    name="primaryAuth"
                                    placeholder="Primary Prior Authorization Number"
                                    component={InputField}
                                />
                            </S.FormField>
                            <S.FormField>
                                <S.Label>Secondary Prior Authorization Number</S.Label>
                                <Field
                                    name="secondaryAuth"
                                    placeholder="Secondary Prior Authorization Number"
                                    component={InputField}
                                />
                            </S.FormField>
                            <S.FormField>
                                <S.Label>Tertiary Prior Authorization Number</S.Label>
                                <Field
                                    name="tertiaryAuth"
                                    placeholder="Tertiary Prior Authorization Number"
                                    component={InputField}
                                />
                            </S.FormField>

                            <S.FormField span={4}>
                                <S.Label>Narrative Description</S.Label>
                                <Field
                                    name="narrative"
                                    placeholder="Narrative Description"
                                    rows={1}
                                    component={AreaField}
                                />
                            </S.FormField>

                            <S.FormField>
                                <BaseCheckbox
                                    name="emergency"
                                    checked={formik.values.emergency}
                                    onChange={e => formik.setFieldValue('emergency', e.target.checked)}
                                    label="Emergency"
                                />
                            </S.FormField>
                        </S.FormGrid>

                        {/* Service Line Drug - collapsible */}
                        <div style={{ marginTop: 16 }}>
                            <CS.SectionHeader>
                                <CS.SectionTitle>Service Line Drug</CS.SectionTitle>
                                <CollapsibleToggleButton
                                    variant="outlined"
                                    type="button"
                                    onClick={(): void => setShowDrug(prev => !prev)}>
                                    <Icon path={showDrug ? mdiChevronDown : mdiChevronRight} size={1} />
                                </CollapsibleToggleButton>
                            </CS.SectionHeader>
                            {showDrug && (
                                <S.FormGrid>
                                    <S.FormField>
                                        <S.Label required>Drug Quantity</S.Label>
                                        <Field
                                            name="drugQuantity"
                                            type="number"
                                            min={0}
                                            step={0.5}
                                            placeholder="Drug Quantity"
                                            component={InputField}
                                        />
                                    </S.FormField>
                                    <S.FormField>
                                        <S.Label required>Drug NDC</S.Label>
                                        <Field
                                            name="drugNdc"
                                            placeholder="National Drug Code (NDC)"
                                            component={InputField}
                                        />
                                    </S.FormField>
                                    <S.FormField>
                                        <S.Label required>Drug Measurement</S.Label>
                                        <Field
                                            name="drugMeasurement"
                                            component={SelectField}
                                            options={drugMeasurementOptions as any}
                                            placeholder="Select"
                                            isClearable={false}
                                        />
                                    </S.FormField>
                                </S.FormGrid>
                            )}
                        </div>

                        {/* Service Line Providers - collapsible */}
                        <div style={{ marginTop: 16 }}>
                            <CS.SectionHeader>
                                <CS.SectionTitle>Service Line Providers</CS.SectionTitle>
                                <CollapsibleToggleButton
                                    variant="outlined"
                                    type="button"
                                    onClick={(): void => setShowProviders(prev => !prev)}>
                                    <Icon path={showProviders ? mdiChevronDown : mdiChevronRight} size={1} />
                                </CollapsibleToggleButton>
                            </CS.SectionHeader>
                            {showProviders && (
                                <div style={{ marginTop: 8 }}>
                                    <ServiceLineProvidersForm
                                        claimEditorStore={claimEditorStore}
                                        serviceIndex={serviceIndex}
                                    />
                                </div>
                            )}
                        </div>

                        {/* Service Line Facility - collapsible */}
                        <div style={{ marginTop: 16 }}>
                            <CS.SectionHeader>
                                <CS.SectionTitle>Service Line Facility</CS.SectionTitle>
                                <CollapsibleToggleButton
                                    variant="outlined"
                                    type="button"
                                    onClick={(): void => setShowFacility(prev => !prev)}>
                                    <Icon path={showFacility ? mdiChevronDown : mdiChevronRight} size={1} />
                                </CollapsibleToggleButton>
                            </CS.SectionHeader>
                            {showFacility && (
                                <div style={{ marginTop: 8 }}>
                                    <ServiceLineFacilityForm
                                        claimEditorStore={claimEditorStore}
                                        serviceIndex={serviceIndex}
                                    />
                                </div>
                            )}
                        </div>
                    </S.FormContainer>
                );
            }}
        </Formik>
    );
};

export default ServiceItemForm;
